import { type ConnectionLineComponentProps,getBezierPath } from "@xyflow/react";
import { type MutableRefObject } from "react";

import type { RuleEdge } from "../types/edges";
import { FOR_EACH_RETURN_VERTICAL_DROP, getForEachReturnPathD } from "./bezierEdgeUtils";

const PILL_WIDTH = 60;
const PILL_HEIGHT = 24;
const HALF_W = PILL_WIDTH / 2;
const HALF_H = PILL_HEIGHT / 2;

const EDGE_LABEL_MAP: Record<string, { label: string; color: string }> = {
	"decision-true": { label: "True", color: "#01D5AB" },
	"decision-false": { label: "False", color: "#DD2C00" },
	"loop-start": { label: "For Each", color: "#01D5AB" },
	"loop-end": { label: "After Last", color: "#7B61FF" },
};

/**
 * React Flow completely unmounts the custom edge component while the user is
 * reconnecting an edge, replacing it with the connection-line. By providing a
 * custom connection-line that reads the reconnecting edge's type from the ref,
 * we can keep the True / False (or For Each / After Last) pill visible during
 * the entire drag.
 */
export const createConnectionLine = (
	reconnectingEdgeRef: MutableRefObject<RuleEdge | null>,
) => {
	const ConnectionLine = ({
		fromX,
		fromY,
		fromPosition,
		toX,
		toY,
		toPosition,
	}: ConnectionLineComponentProps) => {
		const edge = reconnectingEdgeRef.current;
		const isReturn = edge?.type === "loop-for-each-return";

		let path: string;
		let labelX: number;
		let labelY: number;
		if (isReturn) {
			path = getForEachReturnPathD(fromX, fromY, toX, toY).d;
			const cornerY = fromY + FOR_EACH_RETURN_VERTICAL_DROP;
			labelX = fromX;
			labelY = fromY + (cornerY - fromY) * 0.28;
		} else {
			[path, labelX, labelY] = getBezierPath({
				sourceX: fromX,
				sourceY: fromY,
				sourcePosition: fromPosition,
				targetX: toX,
				targetY: toY,
				targetPosition: toPosition,
			});
		}
		const labelData = edge?.type ? EDGE_LABEL_MAP[edge.type] : undefined;

		return (
			<g>
				<path
					d={path}
					fill="none"
					stroke="#DBDCDE"
					strokeWidth={2.5}
				/>

				{labelData && (
					<g
						transform={`translate(${labelX}, ${labelY})`}
						style={{ pointerEvents: "none", userSelect: "none" }}
					>
						<rect
							x={-HALF_W}
							y={-HALF_H}
							width={PILL_WIDTH}
							height={PILL_HEIGHT}
							rx={6}
							ry={6}
							fill="#fff"
						/>
						<rect
							x={-HALF_W}
							y={-HALF_H}
							width={PILL_WIDTH}
							height={PILL_HEIGHT}
							rx={6}
							ry={6}
							fill={labelData.color}
							fillOpacity={0.12}
							stroke={labelData.color}
							strokeWidth={1.5}
						/>
						<text
							textAnchor="middle"
							dominantBaseline="middle"
							style={{
								fontSize: "0.75rem",
								fontWeight: 400,
								fill: labelData.color,
								fontFamily: "Roboto, sans-serif",
							}}
						>
							{labelData.label}
						</text>
					</g>
				)}
			</g>
		);
	};

	return ConnectionLine;
};
